import {
  CONNECTION_PIPELINE_STAGES,
  FIT_QUALITIES,
  RELATIONSHIP_LEVELS,
  RELATIONSHIP_MOTIONS,
  RELATIONSHIP_TYPES,
} from "../root/crmVocabulary.generated";
import {
  clearFollowUp,
  combine,
  markTouched,
  setArrayField,
  setField,
  snoozeFor,
} from "./actions";
import type { SwipeScenario } from "./types";

const label = (
  terms: readonly { value: string; label: string }[],
  value: string,
): string => terms.find((term) => term.value === value)?.label ?? value;

/** Stages that represent an established relationship, per the vocabulary. */
const WARM_STAGES = CONNECTION_PIPELINE_STAGES.filter(
  (stage) => stage.warm,
).map((stage) => stage.value);

/** The FakeRest `@in` parser rejects bare spaces; PostgREST accepts quotes. */
const inList = (values: readonly string[]): string =>
  `(${values.map((value) => `"${value}"`).join(",")})`;

/** Serve the never-reviewed backlog first; see SwipeSort on why this is a filter. */
const NEVER_REVIEWED = { "last_triaged_at@is": null };
const BY_RECENT_CONTACT = {
  field: "last_meaningful_interaction_at",
  order: "DESC",
} as const;
const OLDEST_REVIEW_FIRST = {
  filter: {},
  sort: { field: "last_triaged_at", order: "ASC" },
} as const;

/**
 * The scenarios.
 *
 * Plain data on purpose. Every one runs through the same card, the same
 * gestures and the same undo — a scenario supplies only its filter and its
 * actions, so adding one is a declaration here rather than a change to the
 * engine. The order is the picker's order, and roughly the order the questions
 * are worth answering in: what the relationship IS before what to do about it.
 */
export const SWIPE_SCENARIOS: readonly SwipeScenario[] = [
  {
    id: "strength",
    title: "Relationship level",
    question: "How well do they know you?",
    rationale:
      "The David A. Fields A/B/C scale. Bulk-migrated for most contacts, so most values are a guess until you confirm one.",
    // A and B are only ever set deliberately, so re-asking them wastes the
    // swipe. C is what 20260531000000_david_fields_alignment assigned to
    // everyone it could not classify — 12,470 contacts — so an untriaged C
    // means nobody has looked, and that is the whole queue.
    filter: { ...NEVER_REVIEWED, "status@not.in": '("A","B")' },
    sort: BY_RECENT_CONTACT,
    recycle: OLDEST_REVIEW_FIRST,
    refinements: ["motion", "stage", "group", "staleness"],
    actions: [
      setField("status", "A", {
        label: label(RELATIONSHIP_LEVELS, "A"),
        hint: "They would take your call.",
        tone: "positive",
      }),
      setField("status", "None", {
        label: label(RELATIONSHIP_LEVELS, "None"),
        hint: "Never actually interacted.",
        tone: "negative",
      }),
      setField("status", "B", {
        label: label(RELATIONSHIP_LEVELS, "B"),
        hint: "They would recognise your name.",
        tone: "neutral",
      }),
      setField("status", "C", {
        label: label(RELATIONSHIP_LEVELS, "C"),
        hint: "Peripheral, but a real edge of your network.",
        tone: "special",
      }),
    ],
  },
  {
    id: "fit",
    title: "Fit quality",
    question: "How strong is this, at whatever it is?",
    rationale:
      "Role-agnostic strength, so it is the same question for a buyer and for a partner. Draining it is what makes every other filter sortable.",
    // `unknown` is deliberately not an answer here. It is supposed to carry
    // `fit_missing_evidence` — the one fact that would settle it — and a swipe
    // cannot write one. Skip leaves the record NULL, which is the honest state
    // for "I could not tell", and the import is what writes `unknown`.
    filter: { "fit_quality@is": null },
    sort: BY_RECENT_CONTACT,
    refinements: ["motion", "level", "stage", "reachable"],
    actions: [
      setField("fit_quality", "bullseye", {
        label: label(FIT_QUALITIES, "bullseye"),
        hint: "The best version of this. Spend the attention.",
        tone: "positive",
      }),
      setField("fit_quality", "avoid", {
        label: label(FIT_QUALITIES, "avoid"),
        hint: "Deliberately not worth attention.",
        tone: "negative",
      }),
      setField("fit_quality", "target", {
        label: label(FIT_QUALITIES, "target"),
        hint: "A real fit, without the concentration of a bullseye.",
        tone: "neutral",
      }),
      setField("fit_quality", "stretch", {
        label: label(FIT_QUALITIES, "stretch"),
        hint: "Plausible but unproven. Keep, do not chase.",
        tone: "special",
      }),
    ],
  },
  {
    id: "roles",
    title: "Relationship type",
    question: "What role does this relationship play?",
    rationale:
      "More than one answer is normal, and the array is what makes that sayable — the single-valued ecosystem field forced a delivery partner who also refers into being just one of them.",
    filter: { "relationship_types@is": null },
    sort: BY_RECENT_CONTACT,
    refinements: ["motion", "group", "level", "reachable"],
    // These append, so running the deck again adds a second role rather than
    // overwriting the first. 'client' and 'tool partner' are not offered: they
    // come from the enrichment import and from MCP, and four answers is the
    // ceiling before the deck stops being a gesture.
    actions: [
      setArrayField("relationship_types", ["audience owner"], {
        label: label(RELATIONSHIP_TYPES, "audience owner"),
        hint: "Runs a named podcast, community, event, or publication.",
        tone: "special",
      }),
      setArrayField("relationship_types", [], {
        label: "No role",
        hint: "Opens no door. A decision, not a blank.",
        tone: "negative",
      }),
      setArrayField("relationship_types", ["referral partner"], {
        label: label(RELATIONSHIP_TYPES, "referral partner"),
        hint: "Sends work or introductions.",
        tone: "positive",
      }),
      setArrayField("relationship_types", ["delivery partner"], {
        label: label(RELATIONSHIP_TYPES, "delivery partner"),
        hint: "A firm with client access — co-delivery or white-label.",
        tone: "neutral",
      }),
    ],
  },
  {
    id: "state",
    title: "Current state",
    question: "Where is this relationship really?",
    rationale:
      "Coarse triage over the pipeline stage. Moving a relationship forward needs evidence and belongs on the board; taking dead weight out of the queue does not.",
    filter: NEVER_REVIEWED,
    sort: BY_RECENT_CONTACT,
    recycle: OLDEST_REVIEW_FIRST,
    refinements: ["motion", "stage", "level", "staleness"],
    actions: [
      setField("connection_pipeline_stage", "Active Relationship", {
        label: label(CONNECTION_PIPELINE_STAGES, "Active Relationship"),
        hint: "Live and worth protecting.",
        tone: "positive",
      }),
      setField("connection_pipeline_stage", "Disqualified", {
        label: label(CONNECTION_PIPELINE_STAGES, "Disqualified"),
        hint: "Not going anywhere. Stop counting them.",
        tone: "negative",
      }),
      setField("connection_pipeline_stage", "Dormant", {
        label: label(CONNECTION_PIPELINE_STAGES, "Dormant"),
        hint: "Real, but idle. Recoverable later.",
        tone: "neutral",
      }),
      setField("connection_pipeline_stage", "Archived", {
        label: label(CONNECTION_PIPELINE_STAGES, "Archived"),
        hint: "Out of every queue for good.",
        tone: "special",
      }),
    ],
  },
  {
    id: "motion",
    title: "Motion",
    question: "How do you work this person?",
    rationale:
      "The doctrine treats an unrouted contact as a defect, not a category. This scenario serves those first.",
    filter: { "relationship_motion@eq": "None" },
    sort: BY_RECENT_CONTACT,
    refinements: ["stage", "level", "group", "staleness"],
    actions: [
      setField("relationship_motion", "Nurture", {
        label: label(RELATIONSHIP_MOTIONS, "Nurture"),
        hint: "Already know them. Stay remembered, never pitch.",
        tone: "positive",
      }),
      setField("relationship_motion", "Hold", {
        label: label(RELATIONSHIP_MOTIONS, "Hold"),
        hint: "Park it. No rhythm, no plays.",
        tone: "negative",
      }),
      setField("relationship_motion", "Ecosystem", {
        label: label(RELATIONSHIP_MOTIONS, "Ecosystem"),
        hint: "Owns an audience. The ask is always to teach.",
        tone: "special",
      }),
      setField("relationship_motion", "Conversation Outreach", {
        label: label(RELATIONSHIP_MOTIONS, "Conversation Outreach"),
        hint: "No relationship yet, and you want one.",
        tone: "neutral",
      }),
    ],
  },
  {
    id: "power",
    title: "Buying power",
    question: "What power do they hold?",
    rationale:
      "The second axis of the Fields grid. Paired with relationship level it produces the A1/A2/B1 network core the brief already highlights.",
    filter: NEVER_REVIEWED,
    sort: BY_RECENT_CONTACT,
    recycle: OLDEST_REVIEW_FIRST,
    refinements: ["motion", "level", "stage", "reachable"],
    actions: [
      setField("influence", "1", {
        label: "Decision maker",
        hint: "Holds the purse.",
        tone: "positive",
      }),
      setField("influence", "3", {
        label: "Neither",
        hint: "No budget, no sway.",
        tone: "negative",
      }),
      setField("influence", "2", {
        label: "Influencer",
        hint: "Can refer, connect, or shape the decision.",
        tone: "neutral",
      }),
    ],
  },
  // The two below are what the action abstraction is for: their answers are
  // decisions about what happens next, not labels, and each writes several
  // columns at once.
  {
    id: "recovery",
    title: "Stale warm recovery",
    question: "This one has gone quiet. Now what?",
    rationale:
      "Established relationships with no touch in three months. The doctrine calls losing these the expensive failure, and they are invisible in every other view because nothing about them is wrong.",
    filter: (now) => ({
      "connection_pipeline_stage@in": inList(WARM_STAGES),
      "last_meaningful_interaction_at@lt": new Date(
        now.getTime() - 90 * 86_400_000,
      ).toISOString(),
    }),
    sort: { field: "last_meaningful_interaction_at", order: "ASC" },
    // No staleness refinement: this scenario's own filter already constrains
    // last_meaningful_interaction_at, and a refinement never overrides the
    // scenario's constraint, so the select would sit there doing nothing.
    refinements: ["motion", "level", "stage"],
    actions: [
      combine(
        {
          id: "restart",
          label: "Restart it",
          hint: "Queues the Fields Relationship Restarter for the next few days.",
          tone: "positive",
        },
        setField("suggested_play", "Relationship Restarter", {}),
        snoozeFor(3, {}),
      ),
      setField("connection_pipeline_stage", "Dormant", {
        label: "Let it rest",
        hint: "Real, but not now. Out of the warm queue, still recoverable.",
        tone: "neutral",
      }),
      snoozeFor(90, {
        id: "recovery:defer",
        label: "Not yet",
        hint: "Ask again in three months.",
        tone: "negative",
      }),
    ],
  },
  {
    id: "followup",
    title: "Follow-up due",
    question: "You planned a touch. Did it happen?",
    rationale:
      "Everything with a next-touch date that has come and gone. Answering here is what keeps next_touch_status from becoming decoration.",
    filter: (now) => ({
      "next_touch_date@lt": now.toISOString(),
      "next_touch_status@in": '("Scheduled","Requires Action")',
    }),
    sort: { field: "next_touch_date", order: "ASC" },
    refinements: ["motion", "level", "stage"],
    actions: [
      markTouched({
        id: "followup:done",
        label: "Done — we spoke",
        hint: "Records a meaningful touch today and closes the plan.",
        tone: "positive",
      }),
      snoozeFor(7, {
        id: "followup:push",
        label: "Push a week",
        hint: "Still worth doing, just not today.",
        tone: "neutral",
      }),
      clearFollowUp({
        id: "followup:drop",
        label: "Drop the plan",
        hint: "No touch happened and none is planned. Says so, rather than leaving a stale date.",
        tone: "negative",
      }),
    ],
  },
];

export const getScenario = (
  id: string | undefined,
): SwipeScenario | undefined =>
  SWIPE_SCENARIOS.find((scenario) => scenario.id === id);
